/**
 * Hook — loads upload-attribution records for the active runtime's user
 * and exposes a `Map<canonicalPath, ExecutionLedgerRecord>` for O(1)
 * lookup, plus `resolveFsPath()` to translate an FsBrowser tree path into
 * that map's key. Canonical key is `${caseId}/${blobPath}`.
 *
 * ⚠ THE JOIN IS AUTHORITATIVE NOW — do not reintroduce a regex.
 * This used to parse the caseId out of the tree path with
 * `/cases\/([^/]+)\/(.+)$/`. That was correct for the LEGACY nested shape
 * (`clients/<c>/matters/<m>/cases/<caseId>/…`) and silently WRONG for flat
 * storage (`cases/<slug>--<uid>/…`), where the captured segment is the
 * FOLDER NAME, not the caseId — so it returned a confident wrong identity
 * rather than failing. `case-storage` corrected this lane's first diagnosis
 * of it ("fails silently" was the wrong mechanism; wrong-identity is worse
 * than null) and ruled the real fix is D-21: the registry records
 * `folderName`/`storagePath` at case birth, so the mapping is looked up,
 * never derived.
 *
 * D-21 measured populated on localdev 2026-08-12 (15/15 cases carry both
 * fields; it was 0/174 before the reset), and `GET /api/cases` returns them.
 *
 * If a folder is not in the registry, `resolveFsPath` returns **null** —
 * "I cannot identify this case", which must never be confused with "this
 * file has no upload". A wrong caseId silently attributes one case's upload
 * to another. [[unknown-must-be-representable]]
 *
 * Scope: filtered by the runtime's `userId` (the runtime is per-user,
 * so all uploads visible in this fs view originate from this user OR
 * from system writers that wrote into this user's case workspace).
 * Pagination: walks the cursor until exhausted, but caps at
 * `MAX_PAGES` to avoid runaway loads on very busy users.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { usePlatformApi } from "@/hooks/usePlatformApi";
import type { ExecutionLedgerRecord } from "@/types/execution-ledger";

const PAGE_SIZE = 200;
const MAX_PAGES = 10; // 2_000 upload rows max — TTL is 90d; busy real users may exceed this but the case is rare and the UX is "first 2000 win"

export type AttributionMap = Map<string, ExecutionLedgerRecord>;

export interface UseRuntimeUploadAttributionResult {
  /** Path → record. Path matches `entry.path` from FsBrowser when
   *  root="org" (case-folder-relative). Empty until loaded. */
  attributionMap: AttributionMap;
  /** All raw records, in newest-first order (matches the ledger sort).
   *  Exposed for filter components that need the list independent of
   *  the path-keyed map. */
  records: ExecutionLedgerRecord[];
  loading: boolean;
  error: string | null;
  /** True when the pagination hit `MAX_PAGES` — some uploads MAY be
   *  missing from the map. The UI surfaces a hint so operators aren't
   *  confused by "missing" attribution chips. */
  truncated: boolean;
  /**
   * Translate an FsBrowser tree path into the `${caseId}/${blobPath}` key.
   * Returns `null` when the case cannot be identified — an unresolvable
   * folder, a path outside `cases/`, or the registry not loaded yet.
   * NEVER guesses an identity from the path.
   */
  resolveFsPath: (fsEntryPath: string) => string | null;
}

export function useRuntimeUploadAttribution(
  userId: string | null,
  orgId?: string | null,
): UseRuntimeUploadAttributionResult {
  const api = usePlatformApi();
  const [records, setRecords] = useState<ExecutionLedgerRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    if (!userId) {
      setRecords([]);
      setLoading(false);
      setError(null);
      setTruncated(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setTruncated(false);
      const collected: ExecutionLedgerRecord[] = [];
      let cursor: string | undefined;
      let pagesFetched = 0;
      try {
        while (pagesFetched < MAX_PAGES) {
          const res = await api.listExecutions(
            { userId, kind: "upload" },
            { limit: PAGE_SIZE, cursor },
          );
          if (cancelled) return;
          collected.push(...res.data.records);
          pagesFetched += 1;
          if (!res.data.nextCursor) break;
          cursor = res.data.nextCursor;
        }
        if (cancelled) return;
        setRecords(collected);
        setTruncated(pagesFetched >= MAX_PAGES && Boolean(cursor));
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load upload attribution");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, userId]);

  // Build the canonical-path → record map ONCE per records change.
  // Iterate REVERSE: the ledger returns newest-first, so reverse means
  // newest is written LAST and wins for duplicate paths (re-uploads).
  const attributionMap = useMemo<AttributionMap>(() => {
    const map: AttributionMap = new Map();
    for (let i = records.length - 1; i >= 0; i -= 1) {
      const r = records[i];
      if (!r?.caseId || !r.blobPath) continue;
      map.set(`${r.caseId}/${r.blobPath}`, r);
    }
    return map;
  }, [records]);

  // ── D-21 join: folderName → caseId, from the registry ───────────────
  // Loaded separately from the ledger because it answers a different
  // question: the ledger says WHAT was uploaded, the registry says WHICH
  // case a folder IS. Best-effort — a failure here degrades attribution to
  // "unknown", never to a wrong case.
  const [folderToCaseId, setFolderToCaseId] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!orgId) {
      setFolderToCaseId(new Map());
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.listCases(orgId);
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const c of res.data) {
          // `folderName` is the authoritative on-disk name (D-21). Fall back
          // to the tail of `storagePath` for any row written before the
          // field existed — still a REGISTRY fact, never a path guess.
          const folder = c.folderName ?? c.storagePath?.split("/").pop();
          if (folder) map.set(folder, c.caseId);
        }
        setFolderToCaseId(map);
      } catch {
        // Leave the map empty: unresolvable > wrong.
        if (!cancelled) setFolderToCaseId(new Map());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, orgId]);

  const resolveFsPath = useCallback(
    (fsEntryPath: string) => resolveFsPathWith(folderToCaseId, fsEntryPath),
    [folderToCaseId],
  );

  return { attributionMap, records, loading, error, truncated, resolveFsPath };
}

/**
 * The join itself, as a pure function — exported so it can be tested without a
 * React renderer (this project's vitest is node-env; UI behaviour is covered by
 * Playwright).
 *
 * `folderToCaseId` comes from the case REGISTRY (D-21). Resolution is a lookup,
 * never a parse of identity out of the path:
 *
 *   FLAT    `cases/<slug>--<uid>/<blob>`      → folder looked up
 *   LEGACY  `clients/…/cases/<caseId>/<blob>` → accepted ONLY if the registry
 *                                               confirms that segment is a
 *                                               real caseId
 *
 * Anything else → `null`. Returning null costs an attribution chip; returning a
 * guess attributes one case's upload to another, which is the failure this
 * function exists to make impossible.
 */
export function resolveFsPathWith(
  folderToCaseId: Map<string, string>,
  fsEntryPath: string,
): string | null {
  const m = /(?:^|\/)cases\/([^/]+)\/(.+)$/.exec(fsEntryPath);
  if (!m) return null;
  const [, segment, blobPath] = m;

  const viaRegistry = folderToCaseId.get(segment);
  if (viaRegistry) return `${viaRegistry}/${blobPath}`;

  // Only treat the segment as an id when the registry AGREES it is one.
  for (const caseId of folderToCaseId.values()) {
    if (caseId === segment) return `${segment}/${blobPath}`;
  }
  return null;
}
